import { Draw } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Circle as CircleStyle, Text } from 'ol/style';
import { LineString, Polygon } from 'ol/geom';
import { getArea, getLength } from 'ol/sphere';
import Overlay from 'ol/Overlay';
import { ref, type Ref } from 'vue';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';

import { useMap } from './useMap';

export type MeasureType = 'line' | 'area';

const measuring = ref(false);
const measureType = ref<MeasureType | null>(null);
let measureSource: VectorSource | null = null;
let measureLayer: VectorLayer | null = null;
let drawInteraction: Draw | null = null;
const overlays: Overlay[] = [];

const measureStyle = new Style({
  fill: new Fill({ color: 'rgba(59, 130, 246, 0.15)' }),
  stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [8, 6] }),
  image: new CircleStyle({
    radius: 5,
    fill: new Fill({ color: '#3b82f6' }),
    stroke: new Stroke({ color: '#fff', width: 1.5 }),
  }),
});

/**
 * Composable for line & area measurement — mirrors frontend-next Measurement.vue.
 */
export function useMeasurement() {
  const { map } = useMap();

  const ensureLayer = () => {
    if (!map.value) return;
    if (!measureSource) {
      measureSource = new VectorSource();
      measureLayer = new VectorLayer({
        source: measureSource,
        style: measureStyle,
        zIndex: 900,
      });
      map.value.addLayer(measureLayer);
    }
  };

  const formatLength = (line: LineString): string => {
    const length = getLength(line);
    return length > 1000
      ? `${(length / 1000).toFixed(2)} km`
      : `${length.toFixed(0)} m`;
  };

  const formatArea = (polygon: Polygon): string => {
    const area = getArea(polygon);
    return area > 1_000_000
      ? `${(area / 1_000_000).toFixed(2)} km²`
      : `${area.toFixed(0)} m²`;
  };

  const startMeasure = (type: MeasureType) => {
    if (!map.value) return;
    stopMeasure();
    ensureLayer();

    measureType.value = type;
    measuring.value = true;

    const geomType = type === 'line' ? 'LineString' : 'Polygon';

    drawInteraction = new Draw({
      source: measureSource!,
      type: geomType,
      style: measureStyle,
    });

    let listener: EventsKey | null = null;

    drawInteraction.on('drawstart', (evt) => {
      const sketch = evt.feature;
      listener = sketch.getGeometry()!.on('change', () => {
        // Realtime feedback could go here
      }) as EventsKey;
    });

    drawInteraction.on('drawend', (evt) => {
      const geom = evt.feature.getGeometry()!;
      let output = '';
      let tooltipCoord: number[] = [];

      if (geom instanceof LineString) {
        output = formatLength(geom);
        tooltipCoord = geom.getLastCoordinate();
      } else if (geom instanceof Polygon) {
        output = formatArea(geom);
        tooltipCoord = geom.getInteriorPoint().getCoordinates();
      }

      // Create a permanent measurement label overlay
      const el = document.createElement('div');
      el.className = 'map-kit-measure-tooltip';
      el.textContent = output;

      const overlay = new Overlay({
        element: el,
        offset: [0, -15],
        positioning: 'bottom-center',
        stopEvent: false,
      });
      overlay.setPosition(tooltipCoord);
      map.value!.addOverlay(overlay);
      overlays.push(overlay);

      if (listener) unByKey(listener);
    });

    map.value.addInteraction(drawInteraction);
  };

  const stopMeasure = () => {
    if (drawInteraction && map.value) {
      map.value.removeInteraction(drawInteraction);
      drawInteraction = null;
    }
    measuring.value = false;
    measureType.value = null;
  };

  const clearMeasurements = () => {
    stopMeasure();
    if (measureSource) {
      measureSource.clear();
    }
    overlays.forEach((o) => map.value?.removeOverlay(o));
    overlays.length = 0;
  };

  return {
    measuring,
    measureType: measureType as Ref<MeasureType | null>,
    startMeasure,
    stopMeasure,
    clearMeasurements,
  };
}
